import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import CategoryTable from './child/CategoryTable';
import AddEditCategoryModal from './child/AddEditCategoryModal';
import { Icon } from '@iconify/react';
import config from '../config';
import { useAuth } from '../context/AuthContext';

const CategoriesLayer = () => {
    const { auth } = useAuth();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 1 });
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

    const fetchCategories = useCallback(async (page = 1, searchQuery = '', sort = sortConfig) => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page,
                limit: 20,
                search: searchQuery,
                sort_by: sort.key,
                order: sort.direction
            });
            const response = await fetch(`${config.API_BASE_URL}/api/categories?${queryParams}`);
            const data = await response.json();
            
            if (response.ok) {
                setCategories(data.data);
                setPagination(data.pagination);
            } else {
                toast.error(data.error || 'Failed to fetch categories');
            }
        } catch (error) {
            toast.error('Error connecting to server');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [sortConfig]);

    useEffect(() => {
        fetchCategories(pagination.page, search);
    }, [fetchCategories, pagination.page, search]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 on search
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleAdd = () => {
        setEditingCategory(null);
        setShowModal(true);
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setShowModal(true);
    };

    const handleDelete = async (id, isDefault) => {
        if (isDefault) {
            if (!window.confirm('Warning: This is a default category. Are you sure you want to delete it?')) return;
        } else {
            if (!window.confirm('Are you sure you want to delete this category?')) return;
        }

        try {
            const response = await fetch(`${config.API_BASE_URL}/api/categories/${id}`, {
                method: 'DELETE',
            });
            
            if (response.ok) {
                toast.success('Category deleted successfully');
                fetchCategories(pagination.page, search);
            } else {
                const data = await response.json();
                toast.error(data.error || 'Failed to delete category');
            }
        } catch (error) {
            toast.error('Error deleting category');
        }
    };

    const handleSave = async (formData) => {
        const url = editingCategory 
            ? `${config.API_BASE_URL}/api/categories/${editingCategory.id}`
            : `${config.API_BASE_URL}/api/categories`;
        
        const method = editingCategory ? 'PUT' : 'POST';

        // formData comes as FormData object from the modal now
        // But for backward compatibility or if modal passes object, handle both
        const body = formData instanceof FormData ? formData : JSON.stringify(formData);

        try {
            const response = await auth.fetch(url, {
                method,
                body,
            });
            
            const data = await response.json();

            if (response.ok) {
                toast.success(`Category ${editingCategory ? 'updated' : 'added'} successfully`);
                setShowModal(false);
                fetchCategories(pagination.page, search);
            } else {
                toast.error(data.error || 'Operation failed');
            }
        } catch (error) {
            toast.error('Error saving category');
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center gap-3">
                    <h6 className="text-lg mb-0">Categories</h6>
                    <span className="text-sm fw-medium text-secondary-light border-start ps-3">Total: {pagination.total}</span>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="navbar-search"> 
                        <Icon icon="akar-icons:search" className="icon" />
                        <input 
                            type="text" 
                            className="bg-base h-40-px w-auto" 
                            name="search" 
                            placeholder="Search categories" 
                            value={search}
                            onChange={handleSearchChange}
                        />
                    </div>
                    <button onClick={handleAdd} className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2">
                        <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                        Add New
                    </button>
                </div>
            </div>
            
            <div className="card-body p-24">
                <CategoryTable 
                    categories={categories} 
                    loading={loading} 
                    onEdit={handleEdit} 
                    onDelete={handleDelete}
                    onSort={handleSort}
                    sortConfig={sortConfig}
                />
                
                {/* Pagination */}
                {pagination.total_pages > 1 && (
                    <div className="d-flex align-items-center justify-content-between mt-32">
                        <span className="text-sm text-secondary-light">
                            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                        </span>
                        <ul className="pagination">
                            <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}>
                                    <Icon icon="akar-icons:chevron-left" />
                                </button>
                            </li>
                            {[...Array(pagination.total_pages)].map((_, i) => (
                                <li key={i} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                                    <button className="page-link" onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}>
                                        {i + 1}
                                    </button>
                                </li>
                            ))}
                            <li className={`page-item ${pagination.page === pagination.total_pages ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}>
                                    <Icon icon="akar-icons:chevron-right" />
                                </button>
                            </li>
                        </ul>
                    </div>
                )}
            </div>

            {showModal && (
                <AddEditCategoryModal 
                    show={showModal} 
                    onHide={() => setShowModal(false)} 
                    onSave={handleSave} 
                    category={editingCategory} 
                />
            )}
        </div>
    );
};

export default CategoriesLayer;
